(() => {
  if (customElements.get('sc-cart-drawer')) return;

  const compareMode = new URLSearchParams(window.location.search).get('sc_cart_compare') === '1';

  class ScCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.activeElement = null;
      this.quantityTimer = null;
      this.noteTimer = null;
      this.refreshTimer = null;
      this.headerCartLink = null;
      this.noteRequestId = 0;
      this.openAnimationFrame = null;
      this.focusTimer = null;
      this.pendingFocus = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onHeaderClick = this.onHeaderClick.bind(this);
      this.onHeaderKeyDown = this.onHeaderKeyDown.bind(this);
      this.onExternalCartChange = this.onExternalCartChange.bind(this);
    }

    connectedCallback() {
      if (this.dataset.scCartInitialized === 'true') return;

      this.dataset.scCartInitialized = 'true';
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keyup', this.onKeyUp);
      document.addEventListener('cart:changed', this.onExternalCartChange);
      this.setHeaderCartIconAccessibility();

      if (compareMode) this.enableCompareMode();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.openAnimationFrame);
      window.clearTimeout(this.focusTimer);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('cart:changed', this.onExternalCartChange);

      if (this.headerCartLink) {
        this.headerCartLink.removeEventListener('click', this.onHeaderClick);
        this.headerCartLink.removeEventListener('keydown', this.onHeaderKeyDown);
      }

      delete this.dataset.scCartInitialized;
    }

    isUpcartActive() {
      return Boolean(
        document.getElementById('upCart') ||
          typeof window.upcartGetCart === 'function' ||
          typeof window.upcartOpenCart === 'function'
      );
    }

    shouldYieldToUpcart() {
      if (compareMode) return false;

      if (typeof window.scCartExperiment?.scIsNative === 'function') {
        return !window.scCartExperiment.scIsNative();
      }

      return this.isUpcartActive();
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');

      if (!cartLink || this.headerCartLink === cartLink) return;

      if (this.headerCartLink) {
        this.headerCartLink.removeEventListener('click', this.onHeaderClick);
        this.headerCartLink.removeEventListener('keydown', this.onHeaderKeyDown);
      }

      this.headerCartLink = cartLink;
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', this.onHeaderClick);
      cartLink.addEventListener('keydown', this.onHeaderKeyDown);
    }

    onHeaderClick(event) {
      if (this.shouldYieldToUpcart()) return;

      event.preventDefault();
      this.open(event.currentTarget, { trap: !compareMode, lock: !compareMode });
    }

    onHeaderKeyDown(event) {
      if (event.code !== 'Space' || this.shouldYieldToUpcart()) return;

      event.preventDefault();
      this.open(event.currentTarget, { trap: !compareMode, lock: !compareMode });
    }

    onKeyUp(event) {
      if (event.code === 'Escape' && this.classList.contains('active') && !compareMode) this.close();
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const closeButton = target.closest('[data-sc-cart-close]');

      if (closeButton && this.contains(closeButton)) {
        event.preventDefault();
        this.close();
        return;
      }

      const upsellButton = target.closest('[data-sc-cart-upsell-add]');

      if (upsellButton && this.contains(upsellButton)) {
        event.preventDefault();
        this.addUpsellProduct(upsellButton);
        return;
      }

      const removeButton = target.closest('[data-sc-cart-remove]');

      if (removeButton && this.contains(removeButton)) {
        event.preventDefault();
        const lineItem = removeButton.closest('[data-sc-cart-key]');
        this.updateQuantity(lineItem?.dataset.scCartKey, 0, 'remove', removeButton);
        return;
      }

      const checkoutButton = target.closest('.sc-cart-drawer__checkout');

      if (checkoutButton && this.contains(checkoutButton)) {
        const note = this.querySelector('[data-sc-cart-note]');

        if (note && !this.isNoteValid(note.value)) {
          event.preventDefault();
          this.setNoteSaved(false);
          this.setNoteValidity(note, false);

          const noteDetails = note.closest('.sc-cart-drawer__note');
          if (noteDetails) noteDetails.open = true;

          window.requestAnimationFrame(() => {
            note.focus();
            note.reportValidity();
          });
          return;
        }
      }

      const quantityButton = target.closest('[data-sc-cart-quantity-action]');

      if (!quantityButton || !this.contains(quantityButton)) return;

      event.preventDefault();
      const lineItem = quantityButton.closest('[data-sc-cart-key]');
      const input = lineItem?.querySelector('[data-sc-cart-quantity-input]');

      if (!input) return;

      const currentValue = Number(input.value) || 0;
      const step = Number(input.step) || 1;
      const minimum = Number(input.min) || 0;
      const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const direction = quantityButton.dataset.scCartQuantityAction === 'plus' ? 1 : -1;
      const nextValue = Math.min(maximum, Math.max(minimum, currentValue + direction * step));

      if (nextValue === currentValue) return;

      this.updateQuantity(lineItem.dataset.scCartKey, nextValue, 'quantity', quantityButton);
    }

    onChange(event) {
      const target = event.target;

      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;

      if (target.matches('[data-sc-cart-quantity-input]')) {
        const lineItem = target.closest('[data-sc-cart-key]');

        if (!lineItem) return;

        window.clearTimeout(this.quantityTimer);
        this.quantityTimer = window.setTimeout(() => {
          this.updateQuantity(lineItem.dataset.scCartKey, Number(target.value), 'quantity');
        }, 300);
        return;
      }

    }

    onInput(event) {
      const target = event.target;

      if (!(target instanceof HTMLTextAreaElement) || !target.matches('[data-sc-cart-note]')) return;

      window.clearTimeout(this.noteTimer);
      this.noteRequestId += 1;
      this.setNoteSaved(false);

      if (!this.isNoteValid(target.value)) {
        this.setNoteValidity(target, false);
        return;
      }

      this.setNoteValidity(target, true);
      this.noteTimer = window.setTimeout(() => this.updateNote(target.value, target), 500);
    }

    open(triggeredBy, options = {}) {
      const { trap = true, lock = true } = options;

      if (triggeredBy) this.setActiveElement(triggeredBy);

      if (lock) this.lockPageScroll();

      const activate = () => {
        this.openAnimationFrame = null;
        this.classList.add('active');

        if (!trap) return;

        window.clearTimeout(this.focusTimer);
        this.focusTimer = window.setTimeout(() => {
          if (!this.classList.contains('active')) return;

          const panel = this.querySelector('.sc-cart-drawer__panel');
          const firstFocus = this.querySelector('.sc-cart-drawer__close') || panel;

          if (typeof window.trapFocus === 'function') {
            window.trapFocus(panel, firstFocus);
          } else {
            firstFocus?.focus();
          }
        }, 310);
      };

      if (this.classList.contains('active')) {
        activate();
        return;
      }

      // SC: Commit the closed panel state before activating it so a newly rendered
      // drawer still transitions in instead of appearing at its final position.
      this.querySelector('.sc-cart-drawer__panel')?.getBoundingClientRect();
      window.cancelAnimationFrame(this.openAnimationFrame);
      this.openAnimationFrame = window.requestAnimationFrame(activate);
    }

    close() {
      window.cancelAnimationFrame(this.openAnimationFrame);
      window.clearTimeout(this.focusTimer);
      this.openAnimationFrame = null;
      this.focusTimer = null;
      this.classList.remove('active');
      this.unlockPageScroll();

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.activeElement);
      } else {
        this.activeElement?.focus?.();
      }
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    lockPageScroll() {
      const body = document.body;

      if (body.classList.contains('sc-cart-drawer-open')) return;

      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      body.style.setProperty('--sc-cart-scrollbar-compensation', `${scrollbarWidth}px`);
      body.classList.add('sc-cart-drawer-open');
    }

    unlockPageScroll() {
      const body = document.body;

      body.classList.remove('sc-cart-drawer-open');
      body.style.removeProperty('--sc-cart-scrollbar-compensation');
    }

    getSectionsToRender() {
      return [
        {
          id: 'sc-cart-drawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    renderContents(state) {
      if (!state?.sections) return;

      this.updateRenderedSections(state.sections, { source: 'render-contents' });

      if (!this.shouldYieldToUpcart()) {
        window.setTimeout(() => this.open(null, { trap: !compareMode, lock: !compareMode }));
      }
    }

    updateRenderedSections(sections, { source = 'sc-cart-drawer' } = {}) {
      const drawerHtml = sections['sc-cart-drawer'];
      let drawerUpdated = false;
      let bubbleUpdated = false;

      if (drawerHtml) {
        const documentFragment = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const sourceHost = documentFragment.querySelector('sc-cart-drawer');
        const sourceRoot = documentFragment.querySelector('#ScCartDrawer');
        const targetRoot = this.querySelector('#ScCartDrawer');
        const renderState = this.captureRenderState(targetRoot);

        if (sourceRoot && targetRoot) {
          document.dispatchEvent(
            new CustomEvent('sc:cart-before-render', {
              detail: { drawer: this, source },
            })
          );
          this.reconcileDrawer(sourceRoot, targetRoot);
          this.restoreRenderState(targetRoot, renderState);
          drawerUpdated = true;
        }
        if (sourceHost) {
          this.classList.toggle('is-empty', sourceHost.classList.contains('is-empty'));

          ['scCartItemCount', 'scCartTotalPrice'].forEach((dataKey) => {
            if (sourceHost.dataset[dataKey] === undefined) {
              delete this.dataset[dataKey];
            } else {
              this.dataset[dataKey] = sourceHost.dataset[dataKey];
            }
          });
        }
      }

      const bubbleHtml = sections['cart-icon-bubble'];

      if (bubbleHtml) {
        const documentFragment = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const sourceBubble = documentFragment.querySelector('#cart-icon-bubble');
        const sourceSection = documentFragment.querySelector('.shopify-section');
        const targetBubble = document.querySelector('#cart-icon-bubble');
        const sourceContent = sourceBubble || sourceSection;

        if (sourceContent && targetBubble) {
          const sourceCount = sourceContent.querySelector('.cart-count-bubble');
          const targetCount = targetBubble.querySelector('.cart-count-bubble');
          const targetCountContainer =
            targetBubble.querySelector('.header__icon--cart') || targetBubble;
          const sourceIcon = sourceContent.querySelector('svg.icon-cart, svg.icon-cart-empty');
          const targetIcon = targetBubble.querySelector(':scope > svg.icon-cart, :scope > svg.icon-cart-empty');

          if (sourceCount && targetCount) {
            targetCount.replaceWith(sourceCount);
          } else if (sourceCount) {
            targetCountContainer.append(sourceCount);
          } else {
            targetCount?.remove();
          }

          // SC: The labelled desktop trigger uses a custom image and text. Only a
          // standard top-level cart SVG may be replaced by the section response.
          if (sourceIcon && targetIcon) targetIcon.replaceWith(sourceIcon);
          bubbleUpdated = true;
        }
      }

      this.setHeaderCartIconAccessibility();

      if (drawerUpdated || bubbleUpdated) {
        document.dispatchEvent(
          new CustomEvent('sc:cart-rendered', {
            detail: {
              drawer: this,
              source,
              drawerUpdated,
              bubbleUpdated,
            },
          })
        );
      }
    }

    reconcileDrawer(sourceRoot, targetRoot) {
      const sourceCartBody = sourceRoot.querySelector('.sc-cart-drawer__cart-body');
      const targetCartBody = targetRoot.querySelector('.sc-cart-drawer__cart-body');

      if (sourceCartBody && targetCartBody) {
        this.reconcileCartBody(sourceCartBody, targetCartBody);
      }

      const sourcePanel = sourceRoot.querySelector('.sc-cart-drawer__panel');
      const targetPanel = targetRoot.querySelector('.sc-cart-drawer__panel');

      if (sourcePanel && targetPanel) {
        this.syncOptionalElement(
          sourcePanel.querySelector(':scope > .sc-cart-drawer__footer'),
          targetPanel.querySelector(':scope > .sc-cart-drawer__footer'),
          targetPanel
        );
      }
    }

    reconcileCartBody(sourceCartBody, targetCartBody) {
      const targetRegions = new Map();

      Array.from(targetCartBody.children).forEach((element) => {
        const region = this.getCartBodyRegion(element);
        if (region) targetRegions.set(region, element);
      });

      const retainedRegions = new Set();
      let regionIndex = 0;

      Array.from(sourceCartBody.children).forEach((sourceElement) => {
        const region = this.getCartBodyRegion(sourceElement);

        if (!region) return;

        const targetElement = targetRegions.get(region);
        let resolvedElement;

        if (region === 'items' && targetElement) {
          resolvedElement = this.reconcileCartItemsRegion(sourceElement, targetElement);
        } else if (region === 'shipping' && targetElement) {
          resolvedElement = this.syncShipping(sourceElement, targetElement);
        } else if (targetElement?.isEqualNode(sourceElement)) {
          resolvedElement = targetElement;
        } else {
          resolvedElement = sourceElement.cloneNode(true);

          if (targetElement) targetElement.replaceWith(resolvedElement);
        }

        if (!targetElement) {
          targetCartBody.insertBefore(resolvedElement, targetCartBody.children[regionIndex] || null);
        } else if (targetCartBody.children[regionIndex] !== resolvedElement) {
          targetCartBody.insertBefore(resolvedElement, targetCartBody.children[regionIndex] || null);
        }

        retainedRegions.add(region);
        regionIndex += 1;
      });

      targetRegions.forEach((element, region) => {
        if (!retainedRegions.has(region) && element.isConnected) element.remove();
      });
    }

    getCartBodyRegion(element) {
      if (element.classList.contains('sc-cart-drawer__announcement')) return 'announcement';
      if (element.classList.contains('sc-cart-drawer__shipping')) return 'shipping';
      if (element.classList.contains('sc-cart-drawer__empty')) return 'empty';
      if (element.classList.contains('sc-cart-drawer__body')) return 'items';

      return null;
    }

    reconcileCartItemsRegion(sourceBody, targetBody) {
      const sourceItems = sourceBody.querySelector('[data-sc-cart-items]');
      const targetItems = targetBody.querySelector('[data-sc-cart-items]');

      if (!sourceItems || !targetItems) {
        const replacement = sourceBody.cloneNode(true);
        targetBody.replaceWith(replacement);
        return replacement;
      }

      this.reconcileCartItems(sourceItems, targetItems);

      const sourceForm = sourceBody.querySelector('#ScCartDrawer-Form');
      const targetForm = targetBody.querySelector('#ScCartDrawer-Form');

      if (sourceForm && targetForm) {
        this.syncCartUpsell(
          sourceForm.querySelector(':scope > sc-cart-upsell'),
          targetForm.querySelector(':scope > sc-cart-upsell'),
          targetForm
        );
      }

      return targetBody;
    }

    getNestedCartItemPotUpsell(item) {
      return item?.querySelector(':scope > [data-sc-cart-upsell-key]') || null;
    }

    getNestedCartItemChildren(item) {
      return item?.querySelector(':scope > .sc-cart-drawer-item__children') || null;
    }

    cartItemEqualsIgnoringPot(sourceItem, targetItem) {
      if (!sourceItem || !targetItem) return false;
      if (!this.getNestedCartItemPotUpsell(sourceItem) && !this.getNestedCartItemPotUpsell(targetItem)) {
        return targetItem.isEqualNode(sourceItem);
      }

      const sourceClone = sourceItem.cloneNode(true);
      const targetClone = targetItem.cloneNode(true);

      this.getNestedCartItemPotUpsell(sourceClone)?.remove();
      this.getNestedCartItemPotUpsell(targetClone)?.remove();

      return targetClone.isEqualNode(sourceClone);
    }

    preserveNestedCartItemPotUpsell(targetItem, resolvedItem) {
      const existingUpsell = this.getNestedCartItemPotUpsell(targetItem);
      const clonedUpsell = this.getNestedCartItemPotUpsell(resolvedItem);

      // Server omitted the upsell (e.g. nested pot already present) — drop the old widget.
      if (!clonedUpsell) {
        existingUpsell?.remove();
        return resolvedItem;
      }

      if (existingUpsell) clonedUpsell.replaceWith(existingUpsell);

      return resolvedItem;
    }

    syncNestedCartItemExtras(sourceItem, targetItem) {
      const sourceUpsell = this.getNestedCartItemPotUpsell(sourceItem);
      const targetUpsell = this.getNestedCartItemPotUpsell(targetItem);
      const sourceChildren = this.getNestedCartItemChildren(sourceItem);
      const targetChildren = this.getNestedCartItemChildren(targetItem);

      if (!sourceUpsell) {
        targetUpsell?.remove();
      } else if (!targetUpsell) {
        const upsell = sourceUpsell.cloneNode(true);

        if (targetChildren) targetChildren.after(upsell);
        else targetItem.append(upsell);
      }

      if (sourceChildren) {
        const replacement = sourceChildren.cloneNode(true);

        if (targetChildren) {
          targetChildren.replaceWith(replacement);
        } else {
          const upsell = this.getNestedCartItemPotUpsell(targetItem);

          if (upsell) upsell.before(replacement);
          else targetItem.append(replacement);
        }
      } else if (targetChildren) {
        targetChildren.remove();
      }
    }

    reconcileCartItems(sourceItems, targetItems) {
      const sourceLineItems = Array.from(sourceItems.children).filter((item) =>
        item.matches('[data-sc-cart-key]')
      );
      const targetLineItems = Array.from(targetItems.children).filter((item) =>
        item.matches('[data-sc-cart-key]')
      );
      const targetItemsByKey = new Map(
        targetLineItems.map((item) => [item.dataset.scCartKey, item])
      );
      const retainedKeys = new Set();

      sourceLineItems.forEach((sourceItem, index) => {
        const itemKey = sourceItem.dataset.scCartKey;
        const targetItem = targetItemsByKey.get(itemKey);
        let resolvedItem = targetItem;

        if (!this.cartItemEqualsIgnoringPot(sourceItem, targetItem)) {
          resolvedItem = sourceItem.cloneNode(true);

          if (targetItem) {
            this.preserveNestedCartItemPotUpsell(targetItem, resolvedItem);
            targetItem.replaceWith(resolvedItem);
          }
        } else if (targetItem) {
          this.syncNestedCartItemExtras(sourceItem, targetItem);
        }

        if (!targetItem) {
          targetItems.insertBefore(resolvedItem, targetItems.children[index] || null);
        } else if (targetItems.children[index] !== resolvedItem) {
          targetItems.insertBefore(resolvedItem, targetItems.children[index] || null);
        }

        retainedKeys.add(itemKey);
      });

      targetLineItems.forEach((item) => {
        if (!retainedKeys.has(item.dataset.scCartKey) && item.isConnected) item.remove();
      });
    }

    syncShipping(sourceShipping, targetShipping) {
      if (targetShipping.isEqualNode(sourceShipping)) return targetShipping;

      const previousProgress = this.getRenderedShippingProgress(targetShipping);
      const nextProgress = sourceShipping.style
        .getPropertyValue('--sc-cart-shipping-progress')
        .trim();

      this.syncAttributes(sourceShipping, targetShipping);

      if (targetShipping.innerHTML !== sourceShipping.innerHTML) {
        targetShipping.innerHTML = sourceShipping.innerHTML;
      }

      if (previousProgress !== null && nextProgress) {
        targetShipping.style.setProperty('--sc-cart-shipping-progress', `${previousProgress}%`);
        this.animateShippingProgress(targetShipping, nextProgress);
      }

      return targetShipping;
    }

    syncAttributes(sourceElement, targetElement) {
      Array.from(targetElement.attributes).forEach((attribute) => {
        if (!sourceElement.hasAttribute(attribute.name)) targetElement.removeAttribute(attribute.name);
      });

      Array.from(sourceElement.attributes).forEach((attribute) => {
        targetElement.setAttribute(attribute.name, attribute.value);
      });
    }

    syncOptionalElement(sourceElement, targetElement, targetParent) {
      if (!sourceElement) {
        targetElement?.remove();
        return null;
      }

      if (targetElement?.isEqualNode(sourceElement)) return targetElement;

      const replacement = sourceElement.cloneNode(true);

      if (targetElement) {
        targetElement.replaceWith(replacement);
      } else {
        targetParent.append(replacement);
      }

      return replacement;
    }

    syncCartUpsell(sourceElement, targetElement, targetParent) {
      if (!sourceElement) {
        targetElement?.remove();
        return null;
      }

      if (targetElement && typeof targetElement.refreshRecommendationsFrom === 'function') {
        targetElement.refreshRecommendationsFrom(sourceElement);
        return targetElement;
      }

      return this.syncOptionalElement(sourceElement, targetElement, targetParent);
    }

    findLineItem(itemKey, root = this) {
      return Array.from(root.querySelectorAll('[data-sc-cart-key]')).find(
        (item) => item.dataset.scCartKey === itemKey
      );
    }

    captureRenderState(root) {
      if (!root) return null;

      const activeElement = document.activeElement;
      const noteDetails = root.querySelector('.sc-cart-drawer__note');
      const note = root.querySelector('[data-sc-cart-note]');
      const noteStatus = root.querySelector('[data-sc-cart-note-status]');
      const cartBody = root.querySelector('.sc-cart-drawer__cart-body');

      return {
        scrollTop: cartBody?.scrollTop || 0,
        focus: root.contains(activeElement)
          ? this.getFocusDescriptor(activeElement, root)
          : this.pendingFocus,
        note: note
          ? {
              open: Boolean(noteDetails?.open),
              value: note.value,
              saved: Boolean(noteStatus && !noteStatus.hidden),
            }
          : null,
      };
    }

    getFocusDescriptor(element, root) {
      const item = element.closest('[data-sc-cart-line]');

      if (item) {
        let selector = null;

        if (element.matches('[data-sc-cart-remove]')) selector = '[data-sc-cart-remove]';
        if (element.matches('[data-sc-cart-quantity-input]')) selector = '[data-sc-cart-quantity-input]';
        if (element.matches('[data-sc-cart-quantity-action]')) {
          selector = `[data-sc-cart-quantity-action="${element.dataset.scCartQuantityAction}"]`;
        }
        if (element.matches('.sc-cart-drawer-item__title')) selector = '.sc-cart-drawer-item__title';
        if (element.matches('.sc-cart-drawer-item__image-link')) {
          selector = '.sc-cart-drawer-item__image-link';
        }

        if (selector) {
          const items = Array.from(root.querySelectorAll('[data-sc-cart-line]'));

          return {
            type: 'item',
            key: item.dataset.scCartKey,
            index: items.indexOf(item),
            selector,
          };
        }
      }

      const selectors = [
        '[data-sc-cart-note]',
        '.sc-cart-drawer__note > summary',
        '.sc-cart-drawer__checkout',
        '.sc-cart-drawer__close',
        '.sc-cart-drawer__continue',
      ];
      const selector = selectors.find((candidate) => element.matches(candidate));

      if (!selector) return null;

      return {
        type: 'root',
        selector,
        selectionStart:
          element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement
            ? element.selectionStart
            : null,
        selectionEnd:
          element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement
            ? element.selectionEnd
            : null,
      };
    }

    restoreRenderState(root, state) {
      if (!root || !state) return;

      const cartBody = root.querySelector('.sc-cart-drawer__cart-body');

      if (state.note) {
        const noteDetails = root.querySelector('.sc-cart-drawer__note');
        const note = root.querySelector('[data-sc-cart-note]');
        const noteStatus = root.querySelector('[data-sc-cart-note-status]');

        if (noteDetails) noteDetails.open = state.note.open;

        if (note) {
          note.value = state.note.value;
          this.setNoteValidity(note, this.isNoteValid(note.value));
        }

        if (noteStatus) noteStatus.hidden = !state.note.saved;
      }

      const focus = state.focus;
      let focusTarget = null;

      if (focus?.type === 'item') {
        const items = Array.from(root.querySelectorAll('[data-sc-cart-line]'));
        const item =
          items.find((candidate) => candidate.dataset.scCartKey === focus.key) ||
          items[Math.min(Math.max(focus.index, 0), items.length - 1)];

        focusTarget = item?.querySelector(focus.selector) || null;
      } else if (focus?.type === 'root') {
        focusTarget = root.querySelector(focus.selector);
      }

      if (!focusTarget && focus) focusTarget = root.querySelector('.sc-cart-drawer__close');

      focusTarget?.focus({ preventScroll: true });

      if (
        focusTarget &&
        focus.selectionStart !== null &&
        typeof focusTarget.setSelectionRange === 'function'
      ) {
        focusTarget.setSelectionRange(focus.selectionStart, focus.selectionEnd);
      }

      if (cartBody) cartBody.scrollTop = state.scrollTop;
      this.pendingFocus = null;
    }

    getRenderedShippingProgress(root) {
      const track = root?.querySelector('.sc-cart-drawer__shipping-track');
      const progress = root?.querySelector('.sc-cart-drawer__shipping-progress');
      const trackWidth = track?.getBoundingClientRect().width || 0;

      if (!progress || trackWidth <= 0) return null;

      return Math.min(100, Math.max(0, (progress.getBoundingClientRect().width / trackWidth) * 100));
    }

    animateShippingProgress(shipping, nextProgress) {
      window.requestAnimationFrame(() => {
        if (!shipping.isConnected) return;

        window.requestAnimationFrame(() => {
          if (!shipping.isConnected) return;

          shipping.style.setProperty('--sc-cart-shipping-progress', nextProgress);
        });
      });
    }

    async addUpsellProduct(button) {
      const variantId = Number(button?.dataset.variantId);

      if (!variantId || button.disabled) return;

      const spinner = button.querySelector('[data-sc-cart-upsell-spinner]');
      const errorContainer = button.parentElement?.querySelector('[data-sc-cart-upsell-error]');

      button.disabled = true;
      button.classList.add('is-loading');
      if (spinner) spinner.hidden = false;
      if (errorContainer) {
        errorContainer.textContent = '';
        errorContainer.hidden = true;
      }
      this.clearErrors();

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();

        if (!response.ok || state.errors || state.status) {
          throw new Error(state.description || state.message || state.errors || this.dataset.cartUpdateError);
        }

        this.updateRenderedSections(state.sections || {}, { source: 'sc-cart-drawer-upsell' });

        const announcement = (this.dataset.productAdded || '').replace(
          '[title]',
          button.dataset.productTitle || ''
        );
        this.announce(announcement);

        document.dispatchEvent(
          new CustomEvent('cart:changed', { detail: { source: 'sc-cart-drawer-upsell' } })
        );

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sc-cart-drawer-upsell',
            productVariantId: String(variantId),
          });
        }

        if (compareMode && typeof window.upcartRefreshCart === 'function') {
          Promise.resolve(window.upcartRefreshCart()).catch(() => {});
        }
      } catch (error) {
        const message = error.message || this.dataset.cartUpdateError;

        if (errorContainer) {
          errorContainer.textContent = message;
          errorContainer.hidden = false;
        } else {
          this.showError(message);
        }

        button.disabled = false;
        button.classList.remove('is-loading');
        if (spinner) spinner.hidden = true;
        button.focus({ preventScroll: true });
      }
    }

    async updateQuantity(itemKey, quantity, action, loadingControl = null) {
      if (!itemKey || quantity < 0 || Number.isNaN(quantity)) return;

      const lineItem = this.findLineItem(itemKey);
      const quantityControl = lineItem?.querySelector('[data-sc-cart-quantity]');
      const quantityButtons = Array.from(
        quantityControl?.querySelectorAll('[data-sc-cart-quantity-action]') || []
      );
      const quantityInput = quantityControl?.querySelector('[data-sc-cart-quantity-input]');
      const removeButton = lineItem?.querySelector('[data-sc-cart-remove]');
      const spinner = loadingControl?.querySelector('[data-sc-cart-control-spinner]');
      const root = this.querySelector('#ScCartDrawer');
      const focusElement = loadingControl || document.activeElement;

      if (root?.contains(focusElement)) {
        this.pendingFocus = this.getFocusDescriptor(focusElement, root);
      }

      lineItem?.classList.add('is-loading');
      quantityControl?.classList.add('is-loading');
      quantityButtons.forEach((button) => {
        button.disabled = true;
      });
      if (quantityInput) quantityInput.disabled = true;
      if (removeButton) removeButton.disabled = true;
      loadingControl?.classList.add('is-loading');
      if (spinner) spinner.hidden = false;
      this.clearErrors();

      try {
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();

        if (!response.ok || state.errors || state.status) {
          throw new Error(state.description || state.message || state.errors || this.dataset.cartUpdateError);
        }

        this.updateRenderedSections(state.sections || {}, { source: 'sc-cart-drawer' });
        this.announce(action === 'remove' ? this.dataset.productRemoved : this.dataset.quantityUpdated);

        document.dispatchEvent(new CustomEvent('cart:changed', { detail: { source: 'sc-cart-drawer' } }));

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sc-cart-drawer' });
        }
      } catch (error) {
        this.showError(error.message || this.dataset.cartUpdateError);
        lineItem?.classList.remove('is-loading');
        quantityControl?.classList.remove('is-loading');
        quantityButtons.forEach((button) => {
          button.disabled = false;
        });
        if (quantityInput) quantityInput.disabled = false;
        if (removeButton) removeButton.disabled = false;
        loadingControl?.classList.remove('is-loading');
        if (spinner) spinner.hidden = true;
        loadingControl?.focus({ preventScroll: true });
        this.pendingFocus = null;
      }
    }

    isNoteValid(note) {
      return /^[\p{L}\p{M}\p{N}\p{Zs}\t\r\n.,!?;:'"„“‚‘()\-–—/…]*$/u.test(note);
    }

    setNoteValidity(textarea, isValid) {
      const validationMessage = isValid ? '' : this.dataset.noteValidationError;
      const error = this.querySelector('[data-sc-cart-note-error]');

      textarea.setCustomValidity(validationMessage);
      textarea.setAttribute('aria-invalid', String(!isValid));

      if (error) {
        error.textContent = validationMessage;
        error.hidden = isValid;
      }
    }

    setNoteSaved(isSaved) {
      const status = this.querySelector('[data-sc-cart-note-status]');
      if (status) status.hidden = !isSaved;
    }

    async updateNote(note, textarea) {
      const requestId = ++this.noteRequestId;

      this.clearErrors();

      try {
        const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ note }),
        });

        if (!response.ok) throw new Error(this.dataset.noteUpdateError);

        const currentTextarea = this.querySelector('[data-sc-cart-note]');

        if (requestId !== this.noteRequestId || currentTextarea?.value !== note) return;

        this.setNoteSaved(true);
        this.announce(this.dataset.noteSaved);
      } catch (error) {
        if (requestId !== this.noteRequestId) return;

        this.setNoteSaved(false);
        this.showError(error.message || this.dataset.noteUpdateError);
      }
    }

    onExternalCartChange(event) {
      if (
        event.detail?.source === 'sc-cart-drawer' ||
        event.detail?.source === 'sc-cart-progress-bar'
      ) {
        return;
      }
      if (this.shouldYieldToUpcart()) return;

      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => {
        if (typeof window.scCartExperiment?.scHandleCartMutation === 'function') {
          window.scCartExperiment.scHandleCartMutation({
            source: event.detail?.source || 'cart:changed',
            open: false,
            ignoreDuringSync: true,
          });
          return;
        }

        this.refresh();
      }, 100);
    }

    async refresh() {
      try {
        const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates: {},
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) return;

        const cart = await response.json();
        this.updateRenderedSections(cart.sections || {}, { source: 'cart-changed-fallback' });
      } catch (error) {
        // Keep the last valid drawer markup if a background refresh fails.
      }
    }

    clearErrors() {
      const errorContainer = this.querySelector('#ScCartDrawer-Errors');
      if (errorContainer) errorContainer.textContent = '';
    }

    showError(message) {
      const errorContainer = this.querySelector('#ScCartDrawer-Errors');
      if (errorContainer) errorContainer.textContent = message;
    }

    announce(message) {
      const liveRegion = this.querySelector('#ScCartDrawer-LiveRegion');

      if (!liveRegion) return;

      liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        liveRegion.textContent = message;
      });
    }

    enableCompareMode() {
      document.body.classList.add('sc-cart-compare-mode');
      this.open(null, { trap: false, lock: false });

      let attempts = 0;
      const openBothDrawers = () => {
        attempts += 1;

        if (this.isUpcartActive()) {
          const upcartHost = document.getElementById('upCart');
          const upcartRoot = upcartHost?.shadowRoot || document;
          const upcartPanel = upcartRoot.getElementById?.('CartPopup') || upcartRoot.querySelector?.('#CartPopup');
          const upcartPanelRect = upcartPanel?.getBoundingClientRect();
          const isUpcartOpen = upcartPanelRect && upcartPanelRect.left < window.innerWidth;

          if (isUpcartOpen) return;

          if (typeof window.upcartOpenCart === 'function') {
            Promise.resolve(window.upcartOpenCart()).catch(() => {});
          } else {
            this.headerCartLink?.click();
          }
        }

        if (attempts < 300) window.setTimeout(openBothDrawers, 100);
      };

      window.setTimeout(openBothDrawers, 50);
    }
  }

  class ScCartUpsell extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this.abortController = null;
    }

    connectedCallback() {
      if (this.dataset.scCartUpsellInitialized === 'true') return;

      this.dataset.scCartUpsellInitialized = 'true';

      if (this.hasAttribute('data-sc-cart-upsell-dynamic')) {
        this.loadRecommendations();
      } else {
        this.initSwiper();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.destroySwiper();
      delete this.dataset.scCartUpsellInitialized;
    }

    refreshRecommendationsFrom(sourceElement) {
      const configurationAttributes = [
        'data-sc-cart-upsell',
        'data-cart-product-ids',
        'data-cart-product-handles',
        'data-manual-product-handles',
        'data-upsell-card-background',
        'data-upsell-card-text',
        'data-recommendations-limit',
        'data-recommendation-request-limit',
        'data-recommendations-intent',
        'data-products-limit',
        'data-render-url',
        'data-upsell-previous-label',
        'data-upsell-next-label',
        'style',
      ];

      configurationAttributes.forEach((attributeName) => {
        if (sourceElement.hasAttribute(attributeName)) {
          this.setAttribute(attributeName, sourceElement.getAttribute(attributeName));
        } else {
          this.removeAttribute(attributeName);
        }
      });

      this.setAttribute('data-sc-cart-upsell-dynamic', '');
      this.setAttribute('data-sc-cart-upsell-refreshing', '');
      this.removeAttribute('data-sc-cart-upsell-cache-hit');
      this.removeCartProductSlides();
      this.loadRecommendations();
    }

    removeCartProductSlides() {
      const cartProductIds = new Set(this.parseList(this.dataset.cartProductIds));
      const wrapper = this.querySelector('.sc-cart-drawer__upsell-wrapper');

      if (!cartProductIds.size || !wrapper) return;

      const currentSlides = Array.from(
        wrapper.querySelectorAll(':scope > .sc-cart-drawer__upsell-slide')
      );
      const retainedSlides = currentSlides.filter((slide) => {
        const productId = slide.querySelector(
          '.sc-cart-drawer__upsell-card'
        )?.dataset.productId;

        return !productId || !cartProductIds.has(productId);
      });

      if (retainedSlides.length !== currentSlides.length) {
        this.updateSlides(wrapper, retainedSlides);
      }
    }

    async loadRecommendations() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const cartProductIds = this.uniqueValues(this.parseList(this.dataset.cartProductIds));
      const cartProductHandles = new Set(this.parseList(this.dataset.cartProductHandles));
      const manualProductHandles = this.parseList(this.dataset.manualProductHandles);
      const recommendationsLimit = Number(this.dataset.recommendationsLimit) || 10;
      const recommendationRequestLimit =
        Number(this.dataset.recommendationRequestLimit) || 5;
      const recommendationsIntent = this.dataset.recommendationsIntent || 'related';
      const productsLimit = Number(this.dataset.productsLimit) || 50;
      const upsellCardBackground = this.dataset.upsellCardBackground || '';
      const upsellCardText = this.dataset.upsellCardText || '';
      const cacheKey = this.createCacheKey({
        cartProductIds,
        cartProductHandles: Array.from(cartProductHandles),
        manualProductHandles,
        recommendationsIntent,
        recommendationsLimit,
        recommendationRequestLimit,
        productsLimit,
        upsellCardBackground,
        upsellCardText,
      });
      const cachedEntry = this.readCache(cacheKey);
      const cacheWasApplied = cachedEntry ? this.applyCachedEntry(cachedEntry) : false;

      if (cachedEntry && !cacheWasApplied) this.clearCache(cacheKey);
      if (!cacheWasApplied) this.initSwiper();

      try {
        const recommendationResult = await this.collectProductHandles({
          cartProductIds,
          cartProductHandles,
          manualProductHandles,
          recommendationsIntent,
          recommendationsLimit,
          recommendationRequestLimit,
          productsLimit,
        });
        const finalHandles = recommendationResult.handles;

        if (!this.isConnected) return;

        if (!recommendationResult.complete && cacheWasApplied) {
          this.activateCurrentSlides();
          return;
        }

        if (
          cacheWasApplied &&
          cachedEntry &&
          this.arraysEqual(cachedEntry.handles, finalHandles)
        ) {
          this.activateCurrentSlides();
          return;
        }

        if (!finalHandles.length) {
          if (recommendationResult.complete) this.clearCache(cacheKey);
          this.activateCurrentSlides();
          return;
        }

        const handleGroups = this.chunkValues(finalHandles, 20);
        const renderResults = await Promise.all(
          handleGroups.map((handles) => this.fetchRenderedUpsell(handles))
        );
        const renderedUpsells = renderResults.map((result) => result.upsell);
        const loadedUpsell = renderedUpsells.find(Boolean);

        if (!this.isConnected) return;

        if (renderResults.some((result) => !result.ok)) {
          this.activateCurrentSlides();
          return;
        }

        if (!loadedUpsell) {
          this.clearCache(cacheKey);
          this.remove();
          return;
        }

        const loadedSlides = renderedUpsells.flatMap((upsell) =>
          upsell
            ? Array.from(upsell.querySelectorAll('.sc-cart-drawer__upsell-slide')).map((slide) =>
                slide.cloneNode(true)
              )
            : []
        );

        if (!loadedSlides.length) {
          this.clearCache(cacheKey);
          this.remove();
          return;
        }

        const loadedWrapper = loadedUpsell.querySelector('.sc-cart-drawer__upsell-wrapper');

        if (!loadedWrapper) {
          this.activateCurrentSlides();
          return;
        }

        loadedWrapper.replaceChildren(...loadedSlides);
        this.configureLoadedUpsell(loadedUpsell, loadedSlides.length);
        const loadedSection = loadedUpsell.querySelector('.sc-cart-drawer__upsell');

        if (!loadedSection || !this.applyUpsellSection(loadedSection)) {
          this.activateCurrentSlides();
          return;
        }

        if (recommendationResult.complete) {
          this.writeCache(cacheKey, {
            handles: finalHandles,
            html: loadedSection.outerHTML,
          });
        }
        this.activateCurrentSlides();
      } catch (error) {
        if (error.name !== 'AbortError') this.activateCurrentSlides();
      }
    }

    async collectProductHandles({
      cartProductIds,
      cartProductHandles,
      manualProductHandles,
      recommendationsIntent,
      recommendationsLimit,
      recommendationRequestLimit,
      productsLimit,
    }) {
      const finalHandles = [];
      const seenHandles = new Set();
      let complete = true;
      const appendHandle = (handle) => {
        const normalizedHandle = String(handle || '').trim();

        if (
          !normalizedHandle ||
          cartProductHandles.has(normalizedHandle) ||
          seenHandles.has(normalizedHandle) ||
          finalHandles.length >= productsLimit
        ) {
          return;
        }

        seenHandles.add(normalizedHandle);
        finalHandles.push(normalizedHandle);
      };

      manualProductHandles.forEach(appendHandle);

      let productIndex = 0;
      const maximumProductIndex = Math.min(
        cartProductIds.length,
        recommendationRequestLimit
      );

      while (productIndex < maximumProductIndex && finalHandles.length < productsLimit) {
        const remainingSlots = productsLimit - finalHandles.length;
        const usefulRequestCount = Math.max(
          1,
          Math.ceil(remainingSlots / recommendationsLimit)
        );
        const requestCount = Math.min(
          ScCartUpsell.recommendationBatchSize || 5,
          usefulRequestCount,
          maximumProductIndex - productIndex
        );
        const productIds = cartProductIds.slice(productIndex, productIndex + requestCount);
        const recommendationGroups = await Promise.all(
          productIds.map((productId) =>
            this.fetchRecommendationsForProduct(
              productId,
              recommendationsIntent,
              recommendationsLimit
            )
          )
        );

        productIndex += requestCount;

        for (const products of recommendationGroups) {
          if (!Array.isArray(products)) {
            complete = false;
            continue;
          }

          for (const product of products) {
            appendHandle(product?.handle);
            if (finalHandles.length >= productsLimit) break;
          }

          if (finalHandles.length >= productsLimit) break;
        }
      }

      return { handles: finalHandles, complete };
    }

    async fetchRenderedUpsell(handles) {
      const renderUrl = this.dataset.renderUrl || window.routes?.root_url || '/';
      const separator = renderUrl.includes('?') ? '&' : '?';
      const handlesParameter = handles.map(encodeURIComponent).join(',');

      try {
        const response = await fetch(
          renderUrl + separator + 'sc_cart_upsell_handles=' + handlesParameter,
          {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: this.abortController.signal,
          }
        );

        if (!response.ok) return { ok: false, upsell: null };

        const responseDocument = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );

        return {
          ok: true,
          upsell: responseDocument.querySelector(
            'sc-cart-upsell[data-sc-cart-upsell-loaded]'
          ),
        };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return { ok: false, upsell: null };
      }
    }

    async fetchRecommendationsForProduct(productId, intent, limit) {
      const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
      const endpoint = new URL(
        rootUrl + 'recommendations/products.json',
        window.location.origin
      );

      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('intent', intent);
      endpoint.searchParams.set('limit', String(limit));

      try {
        const response = await fetch(endpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) return null;

        const data = await response.json();
        return Array.isArray(data.products) ? data.products : null;
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return null;
      }
    }

    parseList(value) {
      if (!value) return [];

      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    uniqueValues(values) {
      return Array.from(new Set(values));
    }

    arraysEqual(first, second) {
      return (
        Array.isArray(first) &&
        Array.isArray(second) &&
        first.length === second.length &&
        first.every((value, index) => value === second[index])
      );
    }

    chunkValues(values, size) {
      const chunks = [];

      for (let index = 0; index < values.length; index += size) {
        chunks.push(values.slice(index, index + size));
      }

      return chunks;
    }

    createCacheKey({
      cartProductIds,
      cartProductHandles,
      manualProductHandles,
      recommendationsIntent,
      recommendationsLimit,
      recommendationRequestLimit,
      productsLimit,
      upsellCardBackground,
      upsellCardText,
    }) {
      return JSON.stringify({
        version: ScCartUpsell.cacheVersion || 1,
        theme:
          window.Shopify?.theme?.id ||
          new URLSearchParams(window.location.search).get('preview_theme_id') ||
          '',
        locale: window.Shopify?.locale || document.documentElement.lang || '',
        country: window.Shopify?.country || '',
        currency: window.Shopify?.currency?.active || '',
        cartProductIds,
        cartProductHandles,
        manualProductHandles,
        recommendationsIntent,
        recommendationsLimit,
        recommendationRequestLimit,
        productsLimit,
        upsellCardBackground,
        upsellCardText,
      });
    }

    readCache(cacheKey) {
      let entry = ScCartUpsell.memoryCache;
      const storageKey = ScCartUpsell.cacheStorageKey || 'sc-cart-upsell-cache-v1';
      const cacheTtl = ScCartUpsell.cacheTtl || 10 * 60 * 1000;

      if (!entry || entry.key !== cacheKey) {
        try {
          entry = JSON.parse(window.sessionStorage.getItem(storageKey));
        } catch (_) {
          entry = null;
        }
      }

      if (
        !entry ||
        entry.key !== cacheKey ||
        !Array.isArray(entry.handles) ||
        typeof entry.html !== 'string' ||
        !Number.isFinite(Number(entry.createdAt)) ||
        Date.now() - Number(entry.createdAt) > cacheTtl
      ) {
        if (entry?.key === cacheKey) this.clearCache(cacheKey);
        return null;
      }

      ScCartUpsell.memoryCache = entry;
      return entry;
    }

    writeCache(cacheKey, value) {
      const storageKey = ScCartUpsell.cacheStorageKey || 'sc-cart-upsell-cache-v1';
      const entry = {
        key: cacheKey,
        createdAt: Date.now(),
        handles: value.handles,
        html: value.html,
      };

      ScCartUpsell.memoryCache = entry;

      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(entry));
      } catch (_) {}
    }

    clearCache(cacheKey) {
      const storageKey = ScCartUpsell.cacheStorageKey || 'sc-cart-upsell-cache-v1';

      if (!ScCartUpsell.memoryCache || ScCartUpsell.memoryCache.key === cacheKey) {
        ScCartUpsell.memoryCache = null;
      }

      try {
        const storedEntry = JSON.parse(
          window.sessionStorage.getItem(storageKey)
        );

        if (!storedEntry || storedEntry.key === cacheKey) {
          window.sessionStorage.removeItem(storageKey);
        }
      } catch (_) {
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch (_) {}
      }
    }

    applyCachedEntry(entry) {
      const template = document.createElement('template');

      template.innerHTML = entry.html.trim();
      const section = template.content.querySelector('.sc-cart-drawer__upsell');

      if (!section || !this.applyUpsellSection(section)) return false;

      this.setAttribute('data-sc-cart-upsell-cache-hit', '');
      this.setAttribute('data-sc-cart-upsell-loaded', '');
      return true;
    }

    applyUpsellSection(sourceSection) {
      const sourceSlides = Array.from(
        sourceSection.querySelectorAll('.sc-cart-drawer__upsell-slide')
      );

      if (!sourceSlides.length) return false;

      const currentSection = this.querySelector('.sc-cart-drawer__upsell');

      if (!currentSection) {
        this.destroySwiper();
        this.replaceChildren(sourceSection.cloneNode(true));
        this.configureLoadedUpsell(this, sourceSlides.length);
        this.initSwiper();
        return true;
      }

      const currentWrapper = currentSection.querySelector('.sc-cart-drawer__upsell-wrapper');

      if (!currentWrapper) {
        this.destroySwiper();
        currentSection.replaceWith(sourceSection.cloneNode(true));
        this.configureLoadedUpsell(this, sourceSlides.length);
        this.initSwiper();
        return true;
      }

      this.updateSlides(currentWrapper, sourceSlides);
      return true;
    }

    updateSlides(wrapper, sourceSlides) {
      const currentSlides = Array.from(
        wrapper.querySelectorAll(':scope > .sc-cart-drawer__upsell-slide')
      );
      const previousSlideCount = currentSlides.length;
      const previousActiveIndex = this.swiper?.activeIndex || 0;
      const previousActiveKey = this.getSlideKey(currentSlides[previousActiveIndex]);
      const existingSlides = new Map(
        currentSlides.map((slide) => [this.getSlideKey(slide), slide])
      );
      const nextSlides = sourceSlides.map((sourceSlide) => {
        const key = this.getSlideKey(sourceSlide);
        const existingSlide = existingSlides.get(key);

        return existingSlide?.isEqualNode(sourceSlide)
          ? existingSlide
          : sourceSlide.cloneNode(true);
      });
      const canUpdateExistingSwiper = Boolean(
        this.swiper && previousSlideCount > 1 && nextSlides.length > 1
      );

      if (!canUpdateExistingSwiper) this.destroySwiper();

      wrapper.replaceChildren(...nextSlides);
      this.configureLoadedUpsell(this, nextSlides.length);

      if (canUpdateExistingSwiper) {
        this.swiper.update();
        const nextActiveIndex = nextSlides.findIndex(
          (slide) => this.getSlideKey(slide) === previousActiveKey
        );

        this.swiper.slideTo(
          nextActiveIndex >= 0
            ? nextActiveIndex
            : Math.min(previousActiveIndex, nextSlides.length - 1),
          0,
          false
        );
      } else {
        this.initSwiper();
      }
    }

    getSlideKey(slide) {
      const card = slide?.querySelector('.sc-cart-drawer__upsell-card');

      return card ? `${card.dataset.productId}:${card.dataset.variantId}` : '';
    }

    configureLoadedUpsell(upsell, slideCount) {
      const carousel = upsell.querySelector('.sc-cart-drawer__upsell-carousel');
      const viewport = upsell.querySelector('.sc-cart-drawer__upsell-viewport');

      upsell.dataset.slideCount = String(slideCount);

      if (!carousel || !viewport) return;

      if (slideCount <= 1) {
        carousel.classList.add('is-single');
        viewport.classList.remove('swiper');
        carousel
          .querySelectorAll('[data-sc-cart-upsell-previous], [data-sc-cart-upsell-next]')
          .forEach((button) => button.remove());
        return;
      }

      carousel.classList.remove('is-single');
      viewport.classList.add('swiper');

      if (!carousel.querySelector('[data-sc-cart-upsell-previous]')) {
        carousel.prepend(
          this.createNavigationButton(
            'previous',
            this.dataset.upsellPreviousLabel || 'Previous'
          )
        );
      }

      if (!carousel.querySelector('[data-sc-cart-upsell-next]')) {
        carousel.append(
          this.createNavigationButton('next', this.dataset.upsellNextLabel || 'Next')
        );
      }
    }

    createNavigationButton(direction, label) {
      const isPrevious = direction === 'previous';
      const button = document.createElement('button');

      button.className =
        'sc-cart-drawer__upsell-navigation sc-cart-drawer__upsell-navigation--' + direction;
      button.type = 'button';
      button.dataset[isPrevious ? 'scCartUpsellPrevious' : 'scCartUpsellNext'] = '';
      button.setAttribute('aria-label', label);
      button.innerHTML = isPrevious
        ? '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"></path></svg>'
        : '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"></path></svg>';

      return button;
    }

    activateCurrentSlides() {
      if (!this.isConnected) return;

      if (!this.querySelector('.sc-cart-drawer__upsell-slide')) {
        this.remove();
        return;
      }

      this.removeAttribute('data-sc-cart-upsell-dynamic');
      this.removeAttribute('data-sc-cart-upsell-refreshing');
      this.setAttribute('data-sc-cart-upsell-loaded', '');
      this.initSwiper();
    }

    initSwiper() {
      const swiperElement = this.querySelector('[data-sc-cart-upsell-swiper]');
      const slides = this.querySelectorAll('.sc-cart-drawer__upsell-slide');

      if (!swiperElement || slides.length <= 1) return;

      ScCartUpsell.loadSwiper()
        .then(() => {
          if (!this.isConnected || this.swiper || typeof window.Swiper !== 'function') return;

          this.swiper = new window.Swiper(swiperElement, {
            navigation: {
              nextEl: this.querySelector('[data-sc-cart-upsell-next]'),
              prevEl: this.querySelector('[data-sc-cart-upsell-previous]'),
            },
            observer: true,
            observeParents: true,
            roundLengths: true,
            slidesPerView: 1,
            spaceBetween: 15,
            speed: 300,
            watchOverflow: true,
          });
        })
        .catch(() => {});
    }

    destroySwiper() {
      if (!this.swiper || typeof this.swiper.destroy !== 'function') return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    static loadSwiper() {
      if (typeof window.Swiper === 'function') return Promise.resolve();
      if (ScCartUpsell.swiperLoadingPromise) return ScCartUpsell.swiperLoadingPromise;

      ScCartUpsell.loadSwiperStyles();

      ScCartUpsell.swiperLoadingPromise = new Promise((resolve, reject) => {
        const existingScript = Array.from(document.scripts).find(
          (script) => script.src === ScCartUpsell.swiperScriptUrl
        );
        const script = existingScript || document.createElement('script');

        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', reject, { once: true });

        if (!existingScript) {
          script.src = ScCartUpsell.swiperScriptUrl;
          script.async = true;
          document.head.appendChild(script);
        }
      });

      return ScCartUpsell.swiperLoadingPromise;
    }

    static loadSwiperStyles() {
      const existingStyle = Array.from(document.styleSheets).some(
        (styleSheet) => styleSheet.href === ScCartUpsell.swiperStyleUrl
      );

      if (existingStyle || document.getElementById('sc-cart-upsell-swiper-css')) return;

      const link = document.createElement('link');

      link.id = 'sc-cart-upsell-swiper-css';
      link.rel = 'stylesheet';
      link.href = ScCartUpsell.swiperStyleUrl;
      document.head.appendChild(link);
    }
  }

  ScCartUpsell.swiperLoadingPromise = null;
  ScCartUpsell.memoryCache = null;
  ScCartUpsell.cacheVersion = 1;
  ScCartUpsell.cacheStorageKey = 'sc-cart-upsell-cache-v1';
  ScCartUpsell.cacheTtl = 10 * 60 * 1000;
  ScCartUpsell.recommendationBatchSize = 5;
  ScCartUpsell.swiperScriptUrl = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  ScCartUpsell.swiperStyleUrl = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

  customElements.define('sc-cart-upsell', ScCartUpsell);
  customElements.define('sc-cart-drawer', ScCartDrawer);
})();
